import { CoordSpace, PickSampleExt, View } from "@novorender/api";
import { ReadonlyVec3, vec2, vec3 } from "gl-matrix";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { lastPickSampleActions, useDispatchLastPickSample } from "contexts/lastPickSample";
import { Coordinate, coordinatesActions } from "features/coordinates/coordinatesSlice";
import { selectCoordinates } from "features/coordinates/coordinatesSlice";
import { measureActions } from "features/measure/measureSlice";
import {
    mobileMeasureActions,
    mobileMeasureCursorRef,
    PICK_TARGET_OFFSET,
} from "features/mobileMeasure/mobileMeasureSlice";
import { Picker, renderActions, selectPicker } from "features/render";
import { getSurfaceProjectionTransform } from "features/render/svgUtils";
import { undoRedoActions } from "features/undoRedo";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";
import { useMobileLongPress } from "hooks/useMobileLongPress";
import { useRedirectWheelEvents } from "hooks/useRedirectWheelEvents";
import { selectIsProcoreDesign } from "slices/explorer";
import { hasMouseSupport } from "utils/misc";

import { selectSelectedCoordinateId } from "../mobileMeasure/mobileMeasureSlice";
import { CoordinateLabelHUD } from "./CoordinateLabelHUD";
import { computeLabelPos } from "./coordinateLabelUtils";
import { useAddCoordinate } from "./hooks/useAddCoordinate";

type ComputedCoordinate = {
    id: string;
    coord: Coordinate;
    screenOffset: { x: number; y: number };
    marker: { x: number; y: number };
    label: { x: number; y: number; position: [number, number, number]; svgWidth?: number; positionOnTheSide?: boolean };
    line: { x1: number; y1: number; x2: number; y2: number };
    z: number;
    formattedPos: [string, string, string];
    cssTransform: string;
};

const labelSVGHeight = 16;

export const CoordinatesHtmlInteractions = forwardRef(function CoordinatesHtmlInteractions(_props, ref) {
    const coordinates = useAppSelector(selectCoordinates);
    const {
        state: { view, canvas, size },
    } = useExplorerGlobals();
    const dispatch = useAppDispatch();
    const uom = useUom();
    const picker = useAppSelector(selectPicker);
    const isProcoreDesign = useAppSelector(selectIsProcoreDesign);
    const isInteractive = isProcoreDesign ? picker === Picker.Coordinates : true;
    const addCoordinate = useAddCoordinate();
    const addCoordinateRef = useRef(addCoordinate);
    addCoordinateRef.current = addCoordinate;
    const dispatchLastPickSample = useDispatchLastPickSample();
    const onWheel = useRedirectWheelEvents();
    const selectedCoordinateId = useAppSelector(selectSelectedCoordinateId);

    const coordinatesContainerRef = useRef<HTMLDivElement>(null);

    const computedCoordinatesOverlays = useMemo(() => {
        if (!view) {
            return [];
        }
        const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
        const worldViewMatrixNormal = view?.renderContext?.prevState?.matrices.getMatrixNormal(
            CoordSpace.World,
            CoordSpace.View,
        );

        return coordinates
            .map((coord) => {
                const screen = toScreenSpace(coord.point.position as ReadonlyVec3);
                if (!screen) {
                    return null;
                }
                let cssTransform = "";
                let labelPos = {
                    x: screen[0],
                    y: screen[1] - 45,
                    positionOnTheSide: false,
                };
                if (coord.normal !== undefined) {
                    const originalNormal = vec3.fromValues(coord.normal[0], coord.normal[2], -coord.normal[1]);
                    let normalVS = worldViewMatrixNormal
                        ? vec3.transformMat3(vec3.create(), originalNormal as ReadonlyVec3, worldViewMatrixNormal)
                        : undefined;
                    if (normalVS !== undefined && normalVS.length === 3) {
                        normalVS = vec3.normalize(vec3.create(), normalVS);

                        labelPos = computeLabelPos({
                            normalVS,
                            coord,
                            initialX: screen[0],
                            initialY: screen[1],
                            labelSVGHeight,
                        });
                    }
                    cssTransform = getSurfaceProjectionTransform(
                        normalVS as ReadonlyVec3,
                        screen[0],
                        screen[1],
                        size,
                        view,
                    );
                }
                const zVal = vec3.distance(view.renderState.camera.position, coord.point.position);
                const formattedX = convertAndFormat(uom, coord.point.position[0], UnitType.length, { decimals: 3 });
                const formattedY = convertAndFormat(uom, coord.point.position[1], UnitType.length, { decimals: 3 });
                const formattedZ = convertAndFormat(uom, coord.point.position[2], UnitType.length, { decimals: 3 });
                return {
                    id: coord.id,
                    coord: coord,
                    screenOffset: coord.point.snapType === "normal" ? coord.screenOffset : { x: 0, y: 0 },
                    marker: { x: screen[0], y: screen[1] },
                    label: { x: labelPos.x, y: labelPos.y, position: coord.point.position as [number, number, number] },
                    line: {
                        x1: screen[0],
                        y1: screen[1],
                        x2: labelPos.x,
                        y2: labelPos.y + (labelPos.positionOnTheSide ? labelSVGHeight * 0.5 : 0) + 4,
                    },
                    formattedPos: [formattedX, formattedY, formattedZ],
                    z: zVal,
                    cssTransform,
                } as ComputedCoordinate;
            })
            .filter(Boolean)
            .sort((a, b) => a!.z - b!.z) as ComputedCoordinate[];
    }, [coordinates, view, size, uom]);

    useImperativeHandle(
        ref,
        () => ({
            update: () => {
                const coordinatesContainer = coordinatesContainerRef.current;
                if (!view?.convert || !coordinatesContainer || !computedCoordinatesOverlays.length) {
                    return;
                }
                updateComputedCoordinates2d(view, size.width, size.height, computedCoordinatesOverlays);
                updateElementsPosition(view, computedCoordinatesOverlays, coordinatesContainer);
            },
        }),
        [view, size, computedCoordinatesOverlays],
    );

    useEffect(() => {
        const coordinatesContainer = coordinatesContainerRef.current;
        if (!view || !coordinatesContainer) {
            return;
        }
        // Ensure label layout is updated
        updateElementsPosition(view, computedCoordinatesOverlays, coordinatesContainer);
    }, [view, computedCoordinatesOverlays]);

    const handleDrag = (compCoordinate: ComputedCoordinate) => {
        if (!canvas || !view || !isInteractive) {
            return;
        }
        dispatch(coordinatesActions.removeCoordinate(compCoordinate.id));
        dispatch(renderActions.setPicker(Picker.Coordinates));
        dispatch(coordinatesActions.setDraggingCoordinate(compCoordinate.coord));
        dispatch(coordinatesActions.setHideCoordinateGizmo(false));

        let pick: PickSampleExt | undefined;
        let x = 0;
        let y = 0;

        const stop = async (save: boolean) => {
            canvas.removeEventListener("pointermove", handlePointerMove);
            window.removeEventListener("pointerup", handlePointerUp);
            window.removeEventListener("keydown", handleKeydown);
            dispatch(coordinatesActions.setDraggingCoordinate(null));
            dispatch(renderActions.setPicker(picker));

            let coordinateAdded: Coordinate | false = false;
            try {
                if (save && pick) {
                    coordinateAdded =
                        (await addCoordinateRef.current(pick.position, {
                            screenOffset: {
                                x: pick.x,
                                y: pick.y,
                            },
                            normal: pick.normal,
                            coordinateId: crypto.randomUUID(),
                            objectId: pick.objectId,
                        })) ?? false;
                }
            } catch (ex) {
                console.error("Error adding coordinate:", ex);
            }

            if (!coordinateAdded) {
                dispatch(coordinatesActions.setCoordinates(coordinates));
            } else {
                const newCoord = coordinateAdded;
                const oldCoord = compCoordinate.coord;
                dispatch(
                    undoRedoActions.push({
                        description: "Drag coordinate",
                        undoCallback: () => {
                            dispatch(coordinatesActions.removeCoordinate(newCoord.id));
                            dispatch(coordinatesActions.addCoordinate(oldCoord));
                        },
                        redoCallback: () => {
                            dispatch(coordinatesActions.removeCoordinate(oldCoord.id));
                            dispatch(coordinatesActions.addCoordinate(newCoord));
                        },
                    }),
                );
            }
        };
        const handlePointerMove = async (evt: PointerEvent) => {
            x = evt.offsetX;
            y = evt.offsetY;
            pick = await view.pick(x, y, { async: false });
            dispatchLastPickSample(lastPickSampleActions.set(pick ?? null));
        };
        const handlePointerUp = (e: PointerEvent) => {
            if ((e.target as HTMLElement)?.id === "main-canvas") {
                stop(true);
            } else {
                stop(false);
            }
        };
        const handleKeydown = (evt: KeyboardEvent) => {
            if (evt.key === "Escape") {
                stop(false);
            }
        };
        canvas.addEventListener("pointermove", handlePointerMove);
        window.addEventListener("pointerup", handlePointerUp);
        window.addEventListener("keydown", handleKeydown);
    };

    const handlePointerEnter = () => {
        const coordinatesContainer = coordinatesContainerRef.current;
        if (!coordinatesContainer || !isInteractive) {
            return;
        }

        if (canvas) {
            canvas.style.cursor = "default";
        }
        dispatch(coordinatesActions.setHideCoordinateGizmo(true));
        dispatch(measureActions.selectHoverObj(undefined));
    };

    const handlePointerLeave = () => {
        dispatch(coordinatesActions.setHideCoordinateGizmo(false));
    };

    const hasMouse = hasMouseSupport();

    const handleMobileSelect = (id: string) => {
        dispatch(mobileMeasureActions.setSelectedCoordinateId(id));
    };

    const handleMobileCoordinateLongPress = useCallback(
        (overlay: ComputedCoordinate) => {
            if (!view || !isInteractive) {
                return;
            }
            const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
            const sp = toScreenSpace(overlay.coord.point.position as ReadonlyVec3);
            if (!sp) {
                return;
            }
            mobileMeasureCursorRef.movingCoordinateId = overlay.id;
            dispatch(mobileMeasureActions.setSelectedCoordinateId(overlay.id));
            const dpadX = sp[0] - PICK_TARGET_OFFSET.x;
            const dpadY = sp[1] - PICK_TARGET_OFFSET.y;
            dispatch(mobileMeasureActions.setCursorPosition({ x: dpadX, y: dpadY }));
            dispatch(mobileMeasureActions.setIndicatorPosition({ x: sp[0], y: sp[1] }));
            dispatch(mobileMeasureActions.setCursorActive(true));
            dispatch(mobileMeasureActions.setDpadStyle("normal"));
            dispatch(mobileMeasureActions.setCursorVisual("crosshair"));
        },
        [view, dispatch, isInteractive],
    );

    const handleMobileCoordinateTap = useCallback(
        (overlay: ComputedCoordinate) => {
            handleMobileSelect(overlay.id);
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [],
    );

    const { onPointerDown: handleMobileMarkerDown, onPointerUp: handleMobileMarkerUp } =
        useMobileLongPress<ComputedCoordinate>({
            onTap: handleMobileCoordinateTap,
            onLongPress: handleMobileCoordinateLongPress,
        });

    const handleRemoveCoordinate = (overlay: ComputedCoordinate) => {
        const coord = overlay.coord;
        dispatch(
            undoRedoActions.push({
                description: "Remove coordinate",
                undoCallback: () => {
                    dispatch(coordinatesActions.addCoordinate(coord));
                },
                redoCallback: () => {
                    dispatch(coordinatesActions.removeCoordinate(coord.id));
                },
            }),
        );
        dispatch(coordinatesActions.removeCoordinate(overlay.id));
        dispatch(coordinatesActions.setHideCoordinateGizmo(false));
    };

    return (
        <div onWheel={onWheel} ref={coordinatesContainerRef}>
            {computedCoordinatesOverlays.map((overlay) => (
                <Line
                    key={"coord-" + overlay.id + "-line"}
                    overlay={overlay}
                    onClick={hasMouse || !isInteractive ? undefined : () => handleMobileSelect(overlay.id)}
                />
            ))}
            {computedCoordinatesOverlays.map((overlay) => (
                <Marker
                    key={"coord-" + overlay.id + "-icon"}
                    overlay={overlay}
                    view={view!}
                    onPointerEnter={handlePointerEnter}
                    onPointerLeave={handlePointerLeave}
                    onDrag={hasMouse && isInteractive ? () => handleDrag(overlay) : undefined}
                    onPointerDown={hasMouse || !isInteractive ? undefined : () => handleMobileMarkerDown(overlay)}
                    onPointerUp={hasMouse || !isInteractive ? undefined : handleMobileMarkerUp}
                    isInteractive={isInteractive}
                />
            ))}
            {computedCoordinatesOverlays.map((overlay) => (
                <Label
                    key={"coord-" + overlay.id + "-label"}
                    id={"coord-" + overlay.id + "-label"}
                    overlay={overlay}
                    onPointerEnter={handlePointerEnter}
                    onPointerLeave={handlePointerLeave}
                    onRemove={isInteractive ? () => handleRemoveCoordinate(overlay) : undefined}
                    alwaysShowRemove={!hasMouse && selectedCoordinateId === overlay.id}
                    onClick={hasMouse || !isInteractive ? undefined : () => handleMobileSelect(overlay.id)}
                />
            ))}
        </div>
    );
});
function Marker({
    overlay,
    view,
    onDrag,
    onPointerEnter,
    onPointerLeave,
    onPointerDown,
    onPointerUp,
    isInteractive,
}: {
    overlay: {
        id: string;
        marker: { x: number; y: number };
        cssTransform: string;
        screenOffset: { x: number; y: number };
        coord: Coordinate;
    };
    view: View;
    onDrag?: () => void;
    onPointerEnter: () => void;
    onPointerLeave: () => void;
    onPointerDown?: () => void;
    onPointerUp?: () => void;
    isInteractive: boolean;
}) {
    const snapType = overlay.coord?.point?.snapType;
    const guides = overlay.coord?.point?.guides;

    const icon = getMarkerSvg(view, snapType, guides);
    const xPos = snapType === "normal" ? overlay.marker.x - overlay.screenOffset.x : overlay.marker.x;
    const yPos = snapType === "normal" ? overlay.marker.y + overlay.screenOffset.y : overlay.marker.y;

    return (
        <div
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            id={"coord-" + overlay.id + "-marker"}
            style={{
                position: "absolute",
                left: xPos,
                top: yPos,
                pointerEvents: "auto",
                transform: snapType === "normal" ? overlay.cssTransform : "",
                transformOrigin: "0 0",
                width: 0,
                height: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 11,
            }}
        >
            <div
                style={{
                    backgroundColor: "#003391c0",
                    position: "absolute",
                    width: "100%",
                    height: "100%",
                    borderRadius: "50%",
                    left: -16,
                    top: -16,
                    cursor: isInteractive ? "pointer" : "default",
                    pointerEvents: isInteractive ? "auto" : "none",
                }}
                onPointerDown={(e) => {
                    if (onPointerDown) {
                        onPointerDown();
                    } else if (e.buttons === 1 && onDrag) {
                        onDrag();
                    }
                }}
                onPointerUp={onPointerUp}
                dangerouslySetInnerHTML={{ __html: icon }}
            />
        </div>
    );
}

function Line({
    overlay,
    onClick,
}: {
    overlay: {
        id: string;
        line: { x1: number; y1: number; x2: number; y2: number };
        screenOffset: { x: number; y: number };
    };
    onClick?: () => void;
}) {
    const interactive = !!onClick;
    return (
        <div
            id={"coord-" + overlay.id + "-line-container"}
            key={"coord-" + overlay.id + "-line-container"}
            style={{
                position: "absolute",
                left: overlay.line.x1,
                top: overlay.line.y1,
                pointerEvents: interactive ? "auto" : "none",
                transformOrigin: "0 0",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
            }}
            onClick={onClick}
        >
            <svg style={{ overflow: "visible" }}>
                {interactive && (
                    <line
                        x1={0}
                        y1={0}
                        x2={overlay.line.x2 - overlay.line.x1}
                        y2={overlay.line.y2 - overlay.line.y1 - 4}
                        stroke="transparent"
                        strokeWidth={20}
                        style={{
                            transform: `translate(${-overlay.screenOffset.x}px, ${overlay.screenOffset.y}px)`,
                        }}
                    />
                )}
                <line
                    key={"coord-" + overlay.id + "-line"}
                    id={"coord-" + overlay.id + "-line"}
                    x1={0}
                    y1={0}
                    x2={overlay.line.x2 - overlay.line.x1}
                    y2={overlay.line.y2 - overlay.line.y1 - 4}
                    stroke="#000000ff"
                    strokeWidth={2}
                    style={{
                        transform: `translate(${-overlay.screenOffset.x}px, ${overlay.screenOffset.y}px)`,
                    }}
                />
            </svg>
        </div>
    );
}

export function Label({
    overlay,
    fillColor = "#FFFFFF", // text color
    fillColorBox = "#000000ff", // background color
    id,
    canvas,
    onRemove,
    alwaysShowRemove,
    onPointerEnter,
    onPointerLeave,
    onClick,
}: {
    overlay: {
        id: string;
        label: {
            x: number;
            y: number;
            position: [number, number, number];
            svgWidth?: number;
            positionOnTheSide?: boolean;
        };
        screenOffset: { x: number; y: number };
        formattedPos: [string, string, string];
    };
    id: string;
    fillColor?: string;
    fillColorBox?: string;
    canvas?: HTMLCanvasElement | null;
    onRemove?: () => void;
    alwaysShowRemove?: boolean;
    onPointerEnter: () => void;
    onPointerLeave?: () => void;
    onClick?: () => void;
}) {
    const fontSize = 12;

    return (
        <CoordinateLabelHUD
            id={id}
            formattedPos={overlay.formattedPos}
            fontSize={fontSize}
            labelSVGHeight={labelSVGHeight}
            fillColor={fillColor}
            fillColorBox={fillColorBox}
            onRemove={onRemove}
            alwaysShowRemove={alwaysShowRemove}
            onPointerEnter={onPointerEnter}
            onPointerLeave={onPointerLeave}
            canvas={canvas}
            onClick={onClick}
        />
    );
}

/**
 * Update the 2D coordinates of computed coordinates based on their 3D position and view.
 * This function projects the 3D coordinates to 2D screen space and updates the label positions.
 * @param view The current view context.
 * @param width The width of the canvas.
 * @param height The height of the canvas.
 * @param computedCoordinatesOverlays The list of computed coordinates to update.
 */
function updateComputedCoordinates2d(
    view: View,
    width: number,
    height: number,
    computedCoordinatesOverlays: ComputedCoordinate[],
) {
    if (!view || !computedCoordinatesOverlays.length) {
        return;
    }
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
    const worldViewMatrixNormal = view.renderContext?.prevState?.matrices.getMatrixNormal(
        CoordSpace.World,
        CoordSpace.View,
    );
    for (const coordinateOverlay of computedCoordinatesOverlays) {
        const screen = toScreenSpace(coordinateOverlay.coord.point.position as ReadonlyVec3);
        if (!screen) {
            continue;
        }
        coordinateOverlay.marker = { x: screen[0], y: screen[1] };
        coordinateOverlay.z = vec3.distance(view.renderState.camera.position, coordinateOverlay.coord.point.position);

        let cssTransform = "";
        let labelPos = {
            x: screen[0],
            y: screen[1] - 45,
            positionOnTheSide: false,
        };
        if (coordinateOverlay.coord.normal !== undefined) {
            const originalNormal = vec3.fromValues(
                coordinateOverlay.coord.normal[0],
                coordinateOverlay.coord.normal[2],
                -coordinateOverlay.coord.normal[1],
            );
            let normalVS = worldViewMatrixNormal
                ? vec3.transformMat3(vec3.create(), originalNormal as ReadonlyVec3, worldViewMatrixNormal)
                : undefined;
            if (normalVS !== undefined && normalVS.length === 3) {
                normalVS = vec3.normalize(vec3.create(), normalVS);
                labelPos = computeLabelPos({
                    normalVS,
                    coord: coordinateOverlay.coord,
                    initialX: screen[0],
                    initialY: screen[1],
                    labelSVGHeight,
                });
            }

            coordinateOverlay.label.x = labelPos.x;
            coordinateOverlay.label.y = labelPos.y;
            coordinateOverlay.label.positionOnTheSide = labelPos.positionOnTheSide;
            if (labelPos.positionOnTheSide === true) {
                coordinateOverlay.line = {
                    x1: screen[0],
                    y1: screen[1],
                    x2: labelPos.x,
                    y2: labelPos.y + labelSVGHeight * 0.5 + 4,
                };
            } else {
                coordinateOverlay.line = { x1: screen[0], y1: screen[1], x2: labelPos.x, y2: labelPos.y + 4 };
            }
            cssTransform = getSurfaceProjectionTransform(
                normalVS as ReadonlyVec3,
                screen[0],
                screen[1],
                { width, height },
                view,
            );
        }
        coordinateOverlay.cssTransform = cssTransform;
    }
    computedCoordinatesOverlays.sort((a, b) => a!.z - b!.z);
}

function updateElementsPosition(
    view: View,
    computedCoordinatesOverlays: ComputedCoordinate[],
    coordinatesContainer: HTMLDivElement,
) {
    // Since the elements are sorted by z-index, we can use a simple counter to set the z-index. The first elements in the array will have the highest z-index.
    // This ensures that the elements are rendered in the correct order without needing to sort them again.
    // Labels have different z-index range than markers and lines to ensure they are always on top.
    let labelZIndex = computedCoordinatesOverlays.length * 3;
    let markerAndLineZIndex = computedCoordinatesOverlays.length * 2;
    const labelElements: HTMLElement[] = [];
    const lineElementZIndexList: number[] = [];
    for (const overlay of computedCoordinatesOverlays) {
        const isCoordOnSurface = overlay.coord.point.snapType === "normal";
        const markerElement = coordinatesContainer.querySelector(`#coord-${overlay.id}-marker`) as HTMLElement;
        if (markerElement) {
            const xPos = isCoordOnSurface ? overlay.marker.x - overlay.screenOffset.x : overlay.marker.x;
            const yPos = isCoordOnSurface ? overlay.marker.y + overlay.screenOffset.y : overlay.marker.y;

            markerElement.style.left = `${xPos}px`;
            markerElement.style.top = `${yPos}px`;
            markerElement.style.transform = isCoordOnSurface ? overlay.cssTransform : "none";
            markerElement.style.zIndex = `${markerAndLineZIndex--}`;
            if (!isCoordOnSurface) {
                const svg = markerElement.querySelector("svg");
                if (svg) {
                    svg.outerHTML = getMarkerSvg(view, overlay.coord.point.snapType, overlay.coord.point.guides);
                }
            }
        }

        const labelElement = coordinatesContainer.querySelector(`#coord-${overlay.id}-label`) as HTMLElement;
        if (labelElement) {
            const xPos = isCoordOnSurface ? overlay.label.x - overlay.screenOffset.x : overlay.label.x;
            const yPos = isCoordOnSurface ? overlay.label.y + overlay.screenOffset.y : overlay.label.y;
            labelElement.style.left = `${xPos}px`;
            labelElement.style.top = `${yPos}px`;
            labelElement.style.zIndex = `${labelZIndex--}`;
            labelElements.push(labelElement);
        }

        lineElementZIndexList.push(markerAndLineZIndex--);
    }

    const containerBbox = coordinatesContainer.getBoundingClientRect();

    const labelRects = layoutLabelElements(labelElements, containerBbox);

    // After labels are laid out - we can update the line positions
    computedCoordinatesOverlays.forEach((overlay, i) => {
        const rect = labelRects[i];
        const zIndex = lineElementZIndexList[i];

        overlay.line.x2 = rect.left + (rect.right - rect.left) / 2 - containerBbox.left;
        overlay.line.y2 = rect.top + (rect.bottom - rect.top) / 2 - containerBbox.top;

        const lineElement = coordinatesContainer.querySelector(`#coord-${overlay.id}-line`) as SVGLineElement;
        const lineElementContainer = coordinatesContainer.querySelector(
            `#coord-${overlay.id}-line-container`,
        ) as HTMLDivElement;
        if (lineElementContainer) {
            lineElementContainer.style.left = `${overlay.line.x1}px`;
            lineElementContainer.style.top = `${overlay.line.y1}px`;
            lineElementContainer.style.zIndex = `${zIndex}`;
        }
        if (lineElement) {
            const x2 = overlay.line.x2 - overlay.line.x1;
            const y2 = overlay.label.positionOnTheSide
                ? overlay.line.y2 - overlay.line.y1 - 4
                : overlay.line.y2 - overlay.line.y1;
            lineElement.x1.baseVal.value = 0;
            lineElement.y1.baseVal.value = 0;
            lineElement.x2.baseVal.value = x2;
            lineElement.y2.baseVal.value = y2;
        }
    });
}

// Idea is to go bottom-up and displace up (so only on Y axis) any overlapping labels
// Elements are sorted on reverse Y first to simplify the logic
function layoutLabelElements(labelElements: HTMLElement[], containerBbox: DOMRect) {
    const withRects = labelElements.map((el, i) => {
        const rect = el.getBoundingClientRect();
        return {
            i,
            el,
            rect: {
                originalTop: rect.top,
                top: rect.top,
                bottom: rect.bottom,
                left: rect.left,
                right: rect.right,
            },
        };
    });

    withRects.sort((a, b) => b.rect.top - a.rect.top);

    // Min distance between labels
    const offset = 6;

    for (let i = 0; i < withRects.length - 1; i++) {
        const a = withRects[i];

        for (let j = i + 1; j < withRects.length; j++) {
            const b = withRects[j];

            // Since elements are sorted bottom-up - once we encounter b
            // which is above a - no need to check further
            if (a.rect.top >= b.rect.bottom + offset) {
                break;
            }

            const isOverlapping =
                a.rect.left < b.rect.right + offset &&
                a.rect.right > b.rect.left - offset &&
                a.rect.bottom > b.rect.top - offset;

            if (isOverlapping) {
                // b is always at or higher than a because of the sorting
                const overlapAmount = b.rect.bottom - a.rect.top + offset;
                b.rect.top -= overlapAmount;
                b.rect.bottom -= overlapAmount;
            }
        }
    }

    for (const { el, rect } of withRects) {
        if (rect.top !== rect.originalTop) {
            el.style.top = `${rect.top - containerBbox.top}px`;
        }
    }

    // Revert to original order
    withRects.sort((a, b) => a.i - b.i);

    return withRects.map((r) => r.rect);
}

function getSnapGuides2d(guides3d: ReadonlyVec3[], view: View) {
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false });
    const end2d = toScreenSpace(guides3d[1]);
    const start2d = toScreenSpace(guides3d[0]);
    if (!end2d || !start2d) {
        return null;
    }
    const dir = vec2.sub(vec2.create(), end2d, start2d);
    vec2.normalize(dir, dir);
    vec2.scale(dir, dir, 9); // Scale to make the line longer
    return [dir, vec2.negate(vec2.create(), dir)];
}

function getMarkerSvg(view: View, snapType: string | undefined, guides: ReadonlyVec3[] | undefined) {
    if (snapType === "corner") {
        return `
            <svg width="32" height="32" style="overflow:visible">
                <circle cx="16" cy="16" r="4" fill="#000000ff" stroke="#0F3" stroke-width="2" />
            </svg>
        `;
    } else if (snapType === "midpoint" && guides && guides.length === 2) {
        const pointSnapGuides2d = getSnapGuides2d(guides, view);

        let pathElements: string = "";

        if (pointSnapGuides2d) {
            const rotatedPointSnapGuides2d = pointSnapGuides2d.map((vec) => {
                if (!vec) {
                    return null;
                }
                const rotatedPoint = vec2.rotate(vec2.create(), vec, vec2.create(), Math.PI / 2);
                return rotatedPoint;
            });
            pathElements = rotatedPointSnapGuides2d
                .map((vec) => {
                    if (!vec) {
                        return null;
                    }
                    return `
                    <path
                        d="M16 16l${vec[0]} ${vec[1]}"
                        stroke="black"
                        stroke-opacity="0.38"
                        stroke-width="5"
                        stroke-linecap="round"
                    />
                    <path d="M16 16l${vec[0]} ${vec[1]}" stroke="#0F3" stroke-width="2" stroke-linecap="round" />
                `;
                })
                .join("");
        }
        return `
            <svg width="32" height="32" style="overflow:visible">
                ${pathElements}
                <circle cx="16" cy="16" r="4" fill="#000000ff" stroke="#0F3" stroke-width="2" />
            </svg>
        `;
    } else if (snapType === "tangent" && guides && guides.length === 2) {
        const pointSnapGuides2d = getSnapGuides2d(guides, view);
        let pathElements = "";

        if (pointSnapGuides2d) {
            pathElements = pointSnapGuides2d
                .map((point) => {
                    if (!point) {
                        return null;
                    }
                    return `
                        <path
                            d="M16 16l${point[0]} ${point[1]}"
                            stroke="black"
                            stroke-opacity="0.38"
                            stroke-width="5"
                            stroke-linecap="round"
                        />
                        <path
                            d="M16 16l${point[0]} ${point[1]}"
                            stroke="#0F3"
                            stroke-width="2"
                            stroke-linecap="round"
                        />
                    `;
                })
                .join("");
        }
        return `
            <svg width="32" height="32" style="overflow:visible">
                ${pathElements}
                <circle cx="16" cy="16" r="4" fill="#000000ff" stroke="#0F3" stroke-width="2" />
            </svg>
        `;
    }

    return `
        <svg width="32" height="32" style="overflow:visible">
            <circle cx="16" cy="16" r="13" fill="#333333fb" fill-opacity="0.2" />
            <circle cx="16" cy="16" r="8" fill="#00FF33" />
            <circle cx="16" cy="16" r="3" fill="#000000ff" />
        </svg>
    `;
}
